// API utility functions for authenticated requests

import {
  applyLoginContext,
  clearRoleEntityMap,
  getEntityIdForRole
} from "../auth/roleService";
import { ROLES } from "../config/roles";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.11:3000';

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/** Seller entity id for `/api/seller/*` (not the same as account userId when user also has a buyer profile). */
export const getSellerId = () => {
  return getEntityIdForRole(ROLES.SELLER);
};

// Get user ID from localStorage
export const getUserId = () => {
  return localStorage.getItem('userId');
};

export const setAuth = (data) => {
  localStorage.setItem("authToken", data.token);
  localStorage.setItem("role", data.role);
  localStorage.setItem("user", JSON.stringify(data.user));
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  return token && token !== 'null' && token !== 'undefined';
};

// Clear authentication data
// export const clearAuth = () => {
//   localStorage.removeItem('authToken');
//   localStorage.removeItem('user');
//   localStorage.removeItem('userId');
// };
export const clearAuth = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("role");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem("currentRole");
  localStorage.removeItem("availableRoles");
  clearRoleEntityMap();
};

// Create authenticated FormData request
export const createAuthenticatedFormData = (data = {}) => {
  const formData = new FormData();
  const token = getAuthToken();

  if (token) {
    formData.append('token', token);
  }

  // Add other data
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      formData.append(key, data[key]);
    }
  });

  return formData;
};

// Make authenticated API request with FormData
export const makeAuthenticatedRequest = async (
  endpoint,
  data = {},
  method = 'POST'
) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    let body;

    // ✅ If already FormData → use directly
    if (data instanceof FormData) {
      body = data;
    } else {
      // ✅ Otherwise convert object → FormData
      body = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          body.append(key, data[key]);
        }
      });
    }
    const role = localStorage.getItem("currentRole");

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`, // DO NOT set Content-Type
        "x-role": role,
      },
      body
    });

    const responseData = await response.json();
    // ❌ if API failed → throw error
    if (!response.ok) {
      throw new Error(responseData?.message || "Something went wrong");
    }

    if (
      response.status === 401 ||
      responseData?.message?.toLowerCase().includes('token') ||
      responseData?.message?.toLowerCase().includes('unauthorized')
    ) {
      clearAuth();
      // window.location.href = '/login';
      // throw new Error('Authentication failed');
    }

    return responseData;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * Optional: backend returns `{ seller: "1", buyer: "2", ... }` for the logged-in account.
 * Call after role switch if verify-otp did not return a full map.
 */
export const syncRoleEntityIdsFromServer = async () => {
  try {
    const res = await makeAuthenticatedRequest(
      "/api/auth/role-entities",
      {},
      "POST"
    );
    const raw = res?.data ?? res?.role_entity_ids ?? res;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      applyLoginContext({ role_entity_ids: raw });
    }
  } catch {
    /* endpoint may not exist yet */
  }
};

// --- Admin `/api/admin/*` helpers: same base URL, Bearer token, `x-role` (see `adminXRoleHeader`) ---

async function parseJsonSafeResponse(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { raw: text };
  }
}

/**
 * `requireRole("admin")` expects a lowercase role header; login/API may store mixed case.
 * If `currentRole` is missing (first paint / legacy), default to `admin` for these routes only.
 */
function adminXRoleHeader() {
  const raw = localStorage.getItem("currentRole");
  const s = raw != null && String(raw).trim() !== "" ? String(raw).trim().toLowerCase() : "";
  return s || "admin";
}

/** Some controllers return HTTP 200 with `{ status: 400 }` or `{ success: false }` — surface as errors. */
function assertAdminJsonPayloadOk(data) {
  if (data == null || typeof data !== "object" || Array.isArray(data)) return;
  const st = Number(data.status);
  if (!Number.isNaN(st) && st >= 400) {
    throw new Error(String(data.message || data.error || "Request failed"));
  }
  if (data.success === false) {
    throw new Error(String(data.message || data.error || "Request failed"));
  }
}

/** JSON body; use for admin endpoints that expect `Content-Type: application/json`. */
export const makeAdminJsonRequest = async (endpoint, options = {}) => {
  const { method = "POST", body, query } = options;
  const token = getAuthToken();
  if (!token) throw new Error("No authentication token found");

  let url = `${API_BASE_URL}${endpoint}`;
  if (query && method === "GET") {
    const q = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q.set(k, String(v));
    });
    const s = q.toString();
    if (s) url += `?${s}`;
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    "x-role": adminXRoleHeader()
  };
  const init = { method, headers };

  if (body != null && method !== "GET") {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);
  const data = await parseJsonSafeResponse(response);
  if (!response.ok) {
    throw new Error(data?.message || data?.error || "Something went wrong");
  }
  assertAdminJsonPayloadOk(data);
  return data;
};

export const makeAdminMultipartRequest = async (endpoint, formData, method = "POST") => {
  const token = getAuthToken();
  if (!token) throw new Error("No authentication token found");
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "x-role": adminXRoleHeader()
    },
    body: formData
  });
  const data = await parseJsonSafeResponse(response);
  if (!response.ok) {
    throw new Error(data?.message || "Something went wrong");
  }
  assertAdminJsonPayloadOk(data);
  return data;
};

export const makeAdminFormDataPost = async (endpoint, formData, method = "POST") => {
  const token = getAuthToken();
  if (!token) throw new Error("No authentication token found");
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "x-role": adminXRoleHeader()
    },
    body: formData
  });
  const data = await parseJsonSafeResponse(response);
  if (!response.ok) {
    throw new Error(data?.message || "Something went wrong");
  }
  assertAdminJsonPayloadOk(data);
  return data;
};

export function buildFreightDataTablesFormData({
  draw = 1,
  start = 0,
  length = 25,
  searchValue = "",
  orderDir = "desc"
}) {
  const fd = new FormData();
  fd.append("draw", String(draw));
  fd.append("start", String(start));
  fd.append("length", String(length));
  fd.append("search[value]", searchValue);
  fd.append("order[0][column]", "0");
  fd.append("order[0][dir]", orderDir);
  return fd;
}

export const getVehicleCapacities = () =>
  makeAdminJsonRequest("/api/admin/vehicle-capacities", { method: "POST", body: {} });

/**
 * Freight profiling typeahead. `POST` `/api/admin/freight-profiling/location-search`.
 * Default: cities, states, zones (`data.list` and/or typed arrays). Use `types: "all"` for areas + pincodes in search only.
 *
 * Optional body fields:
 * - `types`: e.g. `"cities"`, `"states"`, `"zones"`, comma lists, or `"all"`.
 * - `zone_id`, `state_id`: cascade filters (states within zone; cities within zone/state).
 * - `city_id`, `state_id`, `zone_id`: optional exact lookup for hydration.
 */
export const searchFreightLocations = (params = {}) => {
  const body = {
    search_text: params.search_text ?? params.search ?? "",
    page_number: params.page_number ?? 1,
    page_size: params.page_size ?? 25
  };
  const types = params.types ?? params.type;
  if (types != null && String(types).trim() !== "") body.types = String(types).trim();
  const z = params.zone_id;
  if (z !== undefined && z !== null && z !== "") body.zone_id = Number(z);
  const st = params.state_id;
  if (st !== undefined && st !== null && st !== "") body.state_id = Number(st);
  const c = params.city_id;
  if (c !== undefined && c !== null && c !== "") body.city_id = Number(c);
  return makeAdminJsonRequest("/api/admin/freight-profiling/location-search", {
    method: "POST",
    body
  });
};

export const verifyFreightGst = (query) =>
  makeAdminJsonRequest("/api/admin/freightUser/verifyGst", { method: "GET", query });

export const checkFreightGstNumber = (body) =>
  makeAdminJsonRequest("/api/admin/freightUser/checkGstNumber", { method: "POST", body });

export const checkFreightTransNumber = (body) =>
  makeAdminJsonRequest("/api/admin/freightUser/checkTransNumber", { method: "POST", body });

/** Legacy DataTables server-side contract (`draw`, `recordsTotal`, …). Prefer {@link getFreightActiveUserList}. */
export const getFreightUserDataTable = (formData) =>
  makeAdminFormDataPost("/api/admin/freightUser/data", formData, "POST");

/**
 * Paginated active verified freight users. Same fields as new/inactive index (`page_number`, `page_size`,
 * `search_text`, optional `sort_by` / `sort_dir`), sent as **JSON body** via `POST` (avoids long URL limits).
 * Response: `{ data: { list, meta } }`.
 */
export const getFreightActiveUserList = (params = {}) => {
  const body = {};
  appendFreightListIndexQuery(body, params);
  const sortBy = params.sort_by != null ? String(params.sort_by).trim() : "";
  if (sortBy) body.sort_by = sortBy;
  const sortDir = params.sort_dir != null ? String(params.sort_dir).trim().toLowerCase() : "";
  if (sortDir === "asc" || sortDir === "desc") body.sort_dir = sortDir;
  return makeAdminJsonRequest("/api/admin/freightActiveUser/list", { method: "POST", body });
};

export const getFreightActiveUserCreate = () =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/create", { method: "GET" });

export const storeFreightActiveUser = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightActiveUser/store", formData);

export const getFreightActiveUserEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightActiveUser/edit/${id}`, { method: "GET" });

export const updateFreightActiveUser = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightActiveUser/update", formData);

export const changeFreightActiveUserStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/changeStatus", { method: "POST", body });

/** Body: { user_id: number, whatsapp_notification: 0|1, app_notification: 0|1 } */
export const updateFreightActiveUserNotificationPrefs = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/updateNotificationPrefs", {
    method: "POST",
    body: {
      user_id: Number(body.user_id),
      whatsapp_notification: Number(body.whatsapp_notification) ? 1 : 0,
      app_notification: Number(body.app_notification) ? 1 : 0
    }
  });

export const getFreightActiveUserProfiling = (id) =>
  makeAdminJsonRequest(`/api/admin/freightActiveUser/${id}/profiling`, { method: "GET" });

export const storeFreightActiveUserProfiling = (id, body) =>
  makeAdminJsonRequest(`/api/admin/freightActiveUser/${id}/profilingStore`, {
    method: "POST",
    body
  });

export const getFreightActiveUserEmployees = async (id) => {
  const token = getAuthToken();
  if (!token) throw new Error("No authentication token found");
  const response = await fetch(
    `${API_BASE_URL}/api/admin/freightActiveUser/${id}/employees`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-role": adminXRoleHeader()
      }
    }
  );
  const data = await parseJsonSafeResponse(response);
  if (!response.ok) throw new Error(data?.message || "Failed to load employees");
  assertAdminJsonPayloadOk(data);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const getFreightActiveUserRmMap = (id) =>
  makeAdminJsonRequest(`/api/admin/freightActiveUser/${id}/rmMap`, { method: "GET" });

export const storeFreightActiveUserRmMap = (id, body) =>
  makeAdminJsonRequest(`/api/admin/freightActiveUser/${id}/rmMapStore`, {
    method: "POST",
    body
  });

export const checkFreightActiveMobile = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/checkMobileNumber", {
    method: "POST",
    body
  });

export const checkFreightActivePan = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/checkPanNumber", { method: "POST", body });

export const checkFreightActiveAadhaar = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/checkAadhaarNumber", {
    method: "POST",
    body
  });

export const updateFreightActiveDocStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/updateDocStatus", { method: "POST", body });

export const getFreightActiveRemarks = (body) =>
  makeAdminJsonRequest("/api/admin/freightActiveUser/getRemarks", { method: "POST", body });

/**
 * Query string for freight admin list index GET routes (backend `parseFreightListQuery` style).
 * Uses `page_number` / `page`, `page_size` / `per_page`, `search_text` (from `search_text` or `search`), optional `all` / `full`.
 * @param {Record<string, string>} query
 * @param {Record<string, unknown>} [params={}]
 */
function appendFreightListIndexQuery(query, params = {}) {
  const pn = params.page_number ?? params.page ?? 1;
  const ps = params.page_size ?? params.per_page ?? 25;
  query.page_number = String(pn);
  query.page_size = String(ps);
  const s =
    params.search_text != null
      ? String(params.search_text).trim()
      : params.search != null
        ? String(params.search).trim()
        : "";
  if (s) query.search_text = s;
  if (params.all === true || String(params.all) === "1" || String(params.full) === "1") {
    query.all = "1";
  }
}

/**
 * Paginated new-user queue. Response: `data.list`, `data.meta` (totals, paging, optional `records_filtered`).
 */
export const getFreightNewUserIndex = (params = {}) => {
  const query = {};
  appendFreightListIndexQuery(query, params);
  const sortBy = params.sort_by != null ? String(params.sort_by).trim() : "";
  if (sortBy) query.sort_by = sortBy;
  const sortDir = params.sort_dir != null ? String(params.sort_dir).trim().toLowerCase() : "";
  if (sortDir === "asc" || sortDir === "desc") query.sort_dir = sortDir;
  return makeAdminJsonRequest("/api/admin/freightNewUser/index", { method: "GET", query });
};

export const getFreightNewUserEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightNewUser/edit/${id}`, { method: "GET" });

export const updateFreightNewUser = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightNewUser/update", formData);

export const changeFreightNewUserStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightNewUser/changeStatus", { method: "POST", body });

export const getFreightNewUserRmMap = (id) =>
  makeAdminJsonRequest(`/api/admin/freightNewUser/${id}/rmMap`, { method: "GET" });

export const storeFreightNewUserRmMap = (id, body) =>
  makeAdminJsonRequest(`/api/admin/freightNewUser/${id}/rmMapStore`, {
    method: "POST",
    body
  });

export const checkFreightNewUserApprovalStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightNewUser/checkUserApprovalStatus", {
    method: "POST",
    body
  });

export const updateFreightNewUserVerifyStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightNewUser/updateVerifyStatus", {
    method: "POST",
    body
  });

/**
 * Inactive freight users index. Same base query as {@link getFreightNewUserIndex}; optional `sort_by` + `sort_dir`
 * when the backend applies dynamic `ORDER BY`.
 */
export const getFreightInactiveUserIndex = (params = {}) => {
  const query = {};
  appendFreightListIndexQuery(query, params);
  const sortBy = params.sort_by != null ? String(params.sort_by).trim() : "";
  if (sortBy) query.sort_by = sortBy;
  const sortDir = params.sort_dir != null ? String(params.sort_dir).trim().toLowerCase() : "";
  if (sortDir === "asc" || sortDir === "desc") query.sort_dir = sortDir;
  return makeAdminJsonRequest("/api/admin/freightInActiveUser/index", {
    method: "GET",
    query
  });
};

/**
 * Active-user list: `updateFreightActiveUserNotificationPrefs`. New/inactive lists still cache toggles in `freightRowNotifyStorage`.
 */

// --- Freight orders (`/api/admin/freightOrder/*`, distance helper) ---

function appendFreightOrderIndexQuery(query, params = {}) {
  const page = params.page ?? params.page_number ?? 1;
  const perPage = params.per_page ?? params.page_size ?? 25;
  const ps = String(Math.min(100, Math.max(1, Number(perPage) || 25)));
  const pn = String(page);
  query.page = pn;
  query.page_number = pn;
  query.per_page = ps;
  query.page_size = ps;
  const sortBy = params.sort_by != null ? String(params.sort_by).trim() : "";
  if (sortBy) query.sort_by = sortBy;
  const sortDir = params.sort_dir != null ? String(params.sort_dir).trim().toLowerCase() : "";
  if (sortDir === "asc" || sortDir === "desc") query.sort_dir = sortDir;
  const searchText =
    params.search_text != null
      ? String(params.search_text).trim()
      : params.search != null
        ? String(params.search).trim()
        : "";
  if (searchText) query.search_text = searchText;
  if (params.all === true || String(params.all) === "1" || String(params.full) === "1") {
    query.all = "1";
  }
}

/** Paginated list. Response: `{ status, data: { list, meta } }`. */
export const getFreightOrderIndex = (params = {}) => {
  const query = {};
  appendFreightOrderIndexQuery(query, params);
  return makeAdminJsonRequest("/api/admin/freightOrder/index", { method: "GET", query });
};

export const getFreightOrderCreateMeta = () =>
  makeAdminJsonRequest("/api/admin/freightOrder/create", { method: "GET" });

export const storeFreightOrder = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/store", { method: "POST", body });

export const getFreightOrderEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightOrder/edit/${id}`, { method: "GET" });

export const getFreightOrderView = (id) =>
  makeAdminJsonRequest(`/api/admin/freightOrder/view/${id}`, { method: "GET" });

export const updateFreightOrder = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/update", { method: "POST", body });

export const deleteFreightOrder = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/delete", { method: "POST", body });

/** Typeahead areas (min 2 chars on server). Returns `{ data: [...] }`. */
export const searchFreightOrderAreas = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/searchAreas", { method: "POST", body });

export const getFreightOrderProcesses = (params = {}) => {
  const gradeId = params.grade_id ?? params.gradeId;
  if (gradeId == null || String(gradeId).trim() === "") return Promise.resolve({ processData: [] });
  return makeAdminJsonRequest("/api/admin/freightOrder/getProcesses", {
    method: "GET",
    query: { grade_id: String(gradeId) }
  });
};

export const getFreightOrderBrands = (params = {}) => {
  const gradeId = params.grade_id ?? params.gradeId;
  const processTypeId = params.process_type_id ?? params.processTypeId;
  if (gradeId == null || processTypeId == null || String(processTypeId).trim() === "") {
    return Promise.resolve({ brandsData: [] });
  }
  const body = { grade_id: gradeId, process_type_id: processTypeId };
  return makeAdminJsonRequest("/api/admin/freightOrder/getBrands", { method: "POST", body });
};

export const getFreightOrderGradeCodes = (params = {}) => {
  const gradeId = params.grade_id ?? params.gradeId;
  const processTypeId = params.process_type_id ?? params.processTypeId;
  const brandId = params.brand_id ?? params.brandId;
  if (gradeId == null || processTypeId == null || brandId == null) {
    return Promise.resolve({ gradeCodeData: [] });
  }
  const body = { grade_id: gradeId, process_type_id: processTypeId, brand_id: brandId };
  return makeAdminJsonRequest("/api/admin/freightOrder/getGradeCodes", { method: "POST", body });
};

/** `id` is **freight_order_details.id** (detail row), not order id. */
export const changeFreightOrderDetailStatus = (detailId, body) =>
  makeAdminJsonRequest(`/api/admin/freightOrder/${detailId}/changeStatus`, { method: "POST", body });

export const storeFreightOrderProposal = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/storeProposal", { method: "POST", body });

export const storeFreightOrderOnlyBSBPrice = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrder/storeOnlyBSBPrice", { method: "POST", body });

/** Query: `from_id`, `to_id` (area ids). */
export const getFreightDistanceDuration = (query) =>
  makeAdminJsonRequest("/api/admin/get-distance-duration", { method: "GET", query });

// --- Freight form requests (`/api/admin/freightFormRequest/*`, DataTables JSON) ---

/** DataTables-style list: POST JSON `{ draw, start, length, search, order, columns }`. */
export const postFreightFormRequestData = async (body) => {
  const data = await makeAdminJsonRequest("/api/admin/freightFormRequest/data", { method: "POST", body });
  if (data && typeof data === "object" && data.error) {
    throw new Error(String(data.error));
  }
  return data;
};

/** Update remarks only. `type`: `buyer` | `seller`; `id`: row id in the respective forms table. */
export const storeFreightFormRequestRemarks = (body) =>
  makeAdminJsonRequest("/api/admin/freightFormRequest/store", { method: "POST", body });

/** Set status (`0` pending, `1` complete, `2` rejected) and remarks. */
export const changeFreightFormRequestStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightFormRequest/changeStatus", { method: "POST", body });

// --- Freight FAQ (`/api/admin/freightFaq/*`) ---

export const getFreightFaqIndex = () =>
  makeAdminJsonRequest("/api/admin/freightFaq/index", { method: "GET" });

export const getFreightFaqCreateMeta = () =>
  makeAdminJsonRequest("/api/admin/freightFaq/create", { method: "GET" });

export const storeFreightFaq = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaq/store", { method: "POST", body });

export const getFreightFaqEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightFaq/edit/${id}`, { method: "GET" });

export const updateFreightFaq = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaq/update", { method: "POST", body });

export const changeFreightFaqStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaq/changeStatus", { method: "POST", body });

// --- Freight FAQ sections (`/api/admin/freightFaqSection/*`) ---

export const getFreightFaqSectionIndex = () =>
  makeAdminJsonRequest("/api/admin/freightFaqSection/index", { method: "GET" });

export const storeFreightFaqSection = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaqSection/store", { method: "POST", body });

export const getFreightFaqSectionEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightFaqSection/edit/${id}`, { method: "GET" });

export const updateFreightFaqSection = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaqSection/update", { method: "POST", body });

export const changeFreightFaqSectionStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightFaqSection/changeStatus", { method: "POST", body });

// --- Freight account under review page slider (`/api/admin/freightAccountUnderReviewPageSlider/*`) ---

export const getFreightAccountUnderReviewSliderIndex = () =>
  makeAdminJsonRequest("/api/admin/freightAccountUnderReviewPageSlider/index", { method: "GET" });

/** `FormData` with repeated `freight_account_under_review_page_slider` file fields (up to 30 files, 8 MB each). */
export const storeFreightAccountUnderReviewSliderImages = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightAccountUnderReviewPageSlider/store", formData, "POST");

export const deleteFreightAccountUnderReviewSliderImage = (imageFileName) =>
  makeAdminJsonRequest("/api/admin/freightAccountUnderReviewPageSlider/deleteImage", {
    method: "POST",
    body: { imageFileName: String(imageFileName) }
  });

// --- Freight profile status master (`/api/admin/freightProfileStatus/*`) ---

export const getFreightProfileStatusIndex = () =>
  makeAdminJsonRequest("/api/admin/freightProfileStatus/index", { method: "GET" });

export const storeFreightProfileStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightProfileStatus/store", { method: "POST", body });

export const getFreightProfileStatusEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightProfileStatus/edit/${id}`, { method: "GET" });

export const updateFreightProfileStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightProfileStatus/update", { method: "POST", body });

export const changeFreightProfileStatusMasterStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightProfileStatus/changeStatus", { method: "POST", body });

// --- Vehicle capacities master (`/api/admin/vehicleCapacitiesMaster/*`) ---

export const getVehicleCapacitiesMasterIndex = (params = {}) =>
  makeAdminJsonRequest("/api/admin/vehicleCapacitiesMaster/index", {
    method: "GET",
    query: {
      sort_by: params.sort_by ?? params.sortBy ?? "",
      sort_dir: params.sort_dir ?? params.sortDir ?? ""
    }
  });

export const storeVehicleCapacitiesMaster = (body) =>
  makeAdminJsonRequest("/api/admin/vehicleCapacitiesMaster/store", { method: "POST", body });

export const getVehicleCapacitiesMasterEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/vehicleCapacitiesMaster/edit/${id}`, { method: "GET" });

export const updateVehicleCapacitiesMaster = (body) =>
  makeAdminJsonRequest("/api/admin/vehicleCapacitiesMaster/update", { method: "POST", body });

export const changeVehicleCapacitiesMasterStatus = (body) =>
  makeAdminJsonRequest("/api/admin/vehicleCapacitiesMaster/changeStatus", { method: "POST", body });

// --- Service category master (`/api/admin/serviceCategoryMaster/*`) ---

export const getServiceCategoryMasterIndex = () =>
  makeAdminJsonRequest("/api/admin/serviceCategoryMaster/index", { method: "GET" });

export const getServiceCategoryMasterActive = () =>
  makeAdminJsonRequest("/api/admin/serviceCategoryMaster/active", { method: "GET" });

export const storeServiceCategoryMaster = (body) =>
  makeAdminJsonRequest("/api/admin/serviceCategoryMaster/store", { method: "POST", body });

export const getServiceCategoryMasterEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/serviceCategoryMaster/edit/${id}`, { method: "GET" });

export const updateServiceCategoryMaster = (body) =>
  makeAdminJsonRequest("/api/admin/serviceCategoryMaster/update", { method: "POST", body });

export const changeServiceCategoryMasterStatus = (body) =>
  makeAdminJsonRequest("/api/admin/serviceCategoryMaster/changeStatus", { method: "POST", body });

// --- Freight order status master (`/api/admin/freightOrderStatus/*`) ---

export const getFreightOrderStatusIndex = () =>
  makeAdminJsonRequest("/api/admin/freightOrderStatus/index", { method: "GET" });

export const storeFreightOrderStatusMaster = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrderStatus/store", { method: "POST", body });

export const getFreightOrderStatusMasterEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightOrderStatus/edit/${id}`, { method: "GET" });

export const updateFreightOrderStatusMaster = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrderStatus/update", { method: "POST", body });

export const changeFreightOrderStatusMasterActive = (body) =>
  makeAdminJsonRequest("/api/admin/freightOrderStatus/changeStatus", { method: "POST", body });

// --- Freight social media (`/api/admin/freightSocialMedia/*`) ---

export const getFreightSocialMediaIndex = () =>
  makeAdminJsonRequest("/api/admin/freightSocialMedia/index", { method: "GET" });

export const storeFreightSocialMedia = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightSocialMedia/store", formData, "POST");

export const getFreightSocialMediaEdit = (id) =>
  makeAdminJsonRequest(`/api/admin/freightSocialMedia/edit/${id}`, { method: "GET" });

export const updateFreightSocialMedia = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightSocialMedia/update", formData, "POST");

export const changeFreightSocialMediaStatus = (body) =>
  makeAdminJsonRequest("/api/admin/freightSocialMedia/changeStatus", { method: "POST", body });

// --- Freight app drawer (`/api/admin/freightAppDrawer/*`) ---

export const getFreightAppDrawerIndex = () =>
  makeAdminJsonRequest("/api/admin/freightAppDrawer/index", { method: "GET" });

export const storeFreightAppDrawer = (body) =>
  makeAdminJsonRequest("/api/admin/freightAppDrawer/store", { method: "POST", body });

// --- Freight RM chat (`/api/admin/freightRmChat/*`) ---

/** Lightweight poll: top N users; empty `search` on paged variant — do not send search here. */
export const getFreightRmChatUsersUpdates = (params = {}) =>
  makeAdminJsonRequest("/api/admin/freightRmChat/users/updates", {
    method: "GET",
    query: { limit: params.limit != null ? String(params.limit) : "30" }
  });

export const getFreightRmChatUsers = () =>
  makeAdminJsonRequest("/api/admin/freightRmChat/users", { method: "GET" });

export const getFreightRmChatUsersPaged = (params = {}) =>
  makeAdminJsonRequest("/api/admin/freightRmChat/users/paged", {
    method: "GET",
    query: {
      page: String(params.page ?? 1),
      per_page: String(params.per_page ?? params.page_size ?? 30),
      search: params.search != null ? String(params.search) : ""
    }
  });

export const getFreightRmChatHistory = (userId) =>
  makeAdminJsonRequest("/api/admin/freightRmChat/history", {
    method: "GET",
    query: { user_id: String(userId) }
  });

export const getFreightRmChatHistoryNew = (params) =>
  makeAdminJsonRequest("/api/admin/freightRmChat/history/new", {
    method: "GET",
    query: {
      user_id: String(params.user_id ?? params.userId),
      lastChatId: String(params.lastChatId ?? params.last_chat_id ?? "")
    }
  });

/** Multipart: `user_id` and/or `last_chat_id`, `message`, optional repeated `files`. */
export const sendFreightRmChatMessage = (formData) =>
  makeAdminMultipartRequest("/api/admin/freightRmChat/send", formData, "POST");

// Make authenticated GET request
export const makeAuthenticatedGetRequest = async (endpoint, params = {}) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Add token to params
    const urlParams = new URLSearchParams({
      token: token,
      ...params
    });

    const response = await fetch(`${API_BASE_URL}${endpoint}?${urlParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const responseData = await response.json();

    // Check if token is invalid or expired
    if (responseData.status === 401 || responseData.message?.includes('token') || responseData.message?.includes('unauthorized')) {
      clearAuth();
      // window.location.href = '/login';
      // throw new Error('Authentication failed');
    }

    return responseData;
  } catch (error) {
    console.error('API GET Request Error:', error);
    throw error;
  }
};

// Specific API functions for common operations

// Get offers/bids
export const getOffers = async (page = 1, limit = 10) => {
  return makeAuthenticatedRequest('/api/offers', { page, limit }, 'POST');
};

// Get requests
export const getRequests = async (page = 1, limit = 10, status = 'new') => {
  return makeAuthenticatedRequest('/api/requests', { page, limit, status }, 'POST');
};

// Get offer details
export const getOfferDetails = async (offerId) => {
  return makeAuthenticatedRequest('/api/offer-details', { offer_id: offerId }, 'POST');
};

// Add price to request
export const addPriceToRequest = async (requestId, price, quantity) => {
  return makeAuthenticatedRequest('/api/add-price', {
    request_id: requestId,
    price,
    quantity
  }, 'POST');
};

// Get buyer responses
export const getBuyerResponses = async (offerId) => {
  return makeAuthenticatedRequest('/api/buyer-responses', { offer_id: offerId }, 'POST');
};

// Get dashboard stats
export const getDashboardStats = async () => {
  return makeAuthenticatedRequest('/api/dashboard-stats', {}, 'POST');
};

// Seller Instant Requests (NEW)
export const getSellerInstantRequests = async (
  sellerId,
  page = 1,
  limit = 10,
  filters = {}
) => {
  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;

  return makeAuthenticatedRequest(
    '/api/seller/instant-request-list',
    payload,
    'POST'
  );
};


// Seller Pending Requests
export const getSellerPendingRequests = async (sellerId, page = 1, limit = 10, filters = {}) => {

  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;
  return makeAuthenticatedRequest(
    '/api/seller/seller-pending-request-list',
    payload,
    'POST'
  );
};

// Seller Accepted Requests
export const getSellerAcceptedRequests = async (sellerId, page = 1, limit = 10, filters = {}) => {

  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;
  return makeAuthenticatedRequest(
    '/api/seller/seller-accepted-request-list',
    payload,
    'POST'
  );
};

// Filter list (grade, brand, process, etc.)
export const getIrmFilters = async (filterType) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-filter-list',
    { filter_type: filterType },
    'POST'
  );
};

// Submit Counter Offer
export const submitCounterOffer = async ({
  sellerId,
  irmRequestId,
  qty,
  price,
  material_location_id,
  offer_availability
}) => {
  // console.log(sellerId, irmRequestId, qty, price, material_location_id, offer_availability);

  return makeAuthenticatedRequest(
    '/api/seller/submit_counter_offer',
    {
      seller_id: sellerId,
      irm_request_id: irmRequestId,
      seller_qty: qty,
      seller_price: price,
      material_location_id: material_location_id,
      offer_availability: offer_availability
    },
    'POST'
  );
};

// Mark IRM request as Not Available
export const markCounterOfferNotAvailable = async (sellerId, irmRequestId) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-counter-offer-not-available',
    {
      seller_id: sellerId,
      irm_request_id: irmRequestId
    },
    'POST'
  );
};

// Cancel IRM counter offer
export const cancelCounterOffer = async (sellerId, irmSellerOfferId) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-cancel-counter-offer',
    {
      seller_id: sellerId,
      seller_request_id: irmSellerOfferId
    },
    'POST'
  );
};

// Modify IRM counter offer
export const modifyCounterOffer = async ({
  sellerId,
  irmRequestId,
  qty,
  price,
  material_location_id
}) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-modify-counter-offer',
    {
      seller_id: sellerId,
      seller_request_id: irmRequestId,
      seller_qty: qty,
      seller_price: price,
      material_location_id
    },
    'POST'
  );
};

// Request call for IRM seller offer
export const requestCallToBuyer = async (sellerId, irmSellerOfferId) => {
  return makeAuthenticatedRequest(
    '/api/seller/call-request-seller',
    {
      seller_id: sellerId,
      irm_seller_offer_id: irmSellerOfferId
    },
    'POST'
  );
};
// Seller notifications
export const getSellerNotifications = async () => {
  const formData = new FormData();
  formData.append('seller_id', getSellerId());
  formData.append('page_size', '10');

  return fetch(`${API_BASE_URL}/api/seller/seller-notifications`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`
    },
    body: formData
  }).then(res => res.json());
};

export const getIrmFilterList = async (filterType) => {


  const formData = new FormData();
  formData.append('filter_type', filterType);
  console.log(formData);

  return makeAuthenticatedRequest(
    '/api/seller/irm-filter-list',
    formData,
    'POST'
  );
};

// ---------------- SELLER OFFERS ----------------

export const getSellerOffers = async ({
  seller_id,
  page_number = 1,
  page_size = 10,
  area_id,
  grade_id
}) => {

  const data = {
    seller_id,
    page_number,
    page_size,
    area_id,
    grade_id
  };

  return makeAuthenticatedRequest(
    "/api/seller/offer-list",
    data
  );
};


export const updateSellerOffers = async (offers) => {

  const token = getAuthToken();
  const role = localStorage.getItem("currentRole");

  const response = await fetch(
    `${API_BASE_URL}/api/seller/post-offers`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-role": role,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ offers })
    }
  );

  return response.json();
};

export const createSellerOffer = async (payload) => {
  return makeAuthenticatedRequest(
    "/api/seller/add-offer",
    payload
  );
};


// ---------------- FILTERS ----------------

export const getGradeFilterList = async (seller_id) => {

  return makeAuthenticatedRequest(
    "/api/seller/filter-grade-list",
    { seller_id }
  );

};


export const getMaterialLocationFilterList = async (seller_id) => {

  return makeAuthenticatedRequest(
    "/api/seller/filter-material-location-list",
    { seller_id, search_text: "" }
  );

};

export const getMaterialList = async ({
  seller_id,
  page_number = 1,
  page_size = 10,
  search_text
}) => {

  return makeAuthenticatedRequest(
    "/api/seller/material-list",
    { seller_id, search_text, page_number, page_size }
  );
};
export const getPolymerList = async () => {
  return makeAuthenticatedRequest(
    "/api/seller/polymer-quality-list"
  );
};
export const getPackagingBagList = async () => {
  return makeAuthenticatedRequest(
    "/api/seller/packaging-bag-list"
  );
};
export const getMaterialLocationList = async ({
  page_number = 1,
  page_size = 10,
  search_text
}) => {

  return makeAuthenticatedRequest(
    "/api/seller/material-location-list",
    { search_text, page_number, page_size }
  );
};


export const getSellerDashboard = async ({ seller_id }) => {
  return makeAuthenticatedRequest(
    "/api/seller/seller-dashboard",
    { seller_id }
  );
};

/** Align paths with your backend; each role may return a different JSON shape — normalize in dashboardService. */
export const getBuyerDashboard = async ({ buyer_id }) => {
  return makeAuthenticatedRequest("/api/buyer/buyer-dashboard", { buyer_id });
};

export const getFreightDashboard = async ({ freight_user_id }) => {
  return makeAuthenticatedRequest("/api/freight/freight-dashboard", {
    freight_user_id
  });
};

export const getAdminDashboard = async ({ admin_id }) => {
  return makeAuthenticatedRequest("/api/admin/admin-dashboard", { admin_id });
};

export const getPriceValidities = async () => {
  return makeAuthenticatedRequest("/api/seller/get-price-validity", {});
};

export const updateAllOfferValidity = async (payload) => {
  return makeAuthenticatedRequest("/api/seller/update-all-offer-validity", payload);
};


export default {
  getAuthToken,
  getSellerId,
  getUserId,
  isAuthenticated,
  clearAuth,
  syncRoleEntityIdsFromServer,
  makeAuthenticatedRequest,
  makeAuthenticatedGetRequest,
  getOffers,
  getRequests,
  getOfferDetails,
  addPriceToRequest,
  getBuyerResponses,
  getDashboardStats,
  getSellerInstantRequests,
  getSellerPendingRequests,
  getSellerAcceptedRequests,
  getIrmFilters,
  submitCounterOffer,
  markCounterOfferNotAvailable,
  cancelCounterOffer,
  modifyCounterOffer,
  requestCallToBuyer,
  getSellerNotifications,
  getIrmFilterList,
  getSellerOffers,
  updateSellerOffers,
  getGradeFilterList,
  getMaterialLocationFilterList,
  getMaterialList,
  getPolymerList,
  getPackagingBagList,
  getMaterialLocationList,
  getSellerDashboard,
  getBuyerDashboard,
  getFreightDashboard,
  getAdminDashboard,
  getPriceValidities,
  updateAllOfferValidity
};